import { useState } from 'react';
import { X, Upload, FileText, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const PlanUploadModal = ({ isOpen, onClose }) => {
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    projectType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    // Simulate upload
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setUploading(false);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFiles([]);
      setFormData({ name: '', email: '', phone: '', postcode: '', projectType: '', message: '' });
      onClose();
    }, 3000);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-[#0F172A] mb-2">Plans Received!</h3>
          <p className="text-[#475569]">
            We'll review your plans and get back to you within 24 hours with a detailed quotation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full my-8" data-testid="plan-upload-modal">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-[#0F172A]">Upload Your Plans</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="plan-upload-close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dropzone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#0066FF] transition-colors"
            data-testid="plan-upload-dropzone"
          >
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-[#0F172A] font-semibold mb-2">
              Drop your plans here or click to browse
            </p>
            <p className="text-sm text-[#475569] mb-4">
              PDF, JPG, PNG up to 10MB each
            </p>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button type="button" variant="outline" className="cursor-pointer" onClick={() => document.getElementById('file-upload').click()}>
                Select Files
              </Button>
            </label>
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-[#0066FF]" />
                    <span className="text-sm text-[#0F172A]">{file.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Contact Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plan-name">Name *</Label>
              <Input
                id="plan-name"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="plan-upload-name"
              />
            </div>
            <div>
              <Label htmlFor="plan-phone">Phone *</Label>
              <Input
                id="plan-phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="plan-upload-phone"
              />
            </div>
            <div>
              <Label htmlFor="plan-email">Email *</Label>
              <Input
                id="plan-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="plan-upload-email"
              />
            </div>
            <div>
              <Label htmlFor="plan-postcode">Postcode *</Label>
              <Input
                id="plan-postcode"
                required
                value={formData.postcode}
                onChange={(e) => setFormData({...formData, postcode: e.target.value.toUpperCase()})}
                data-testid="plan-upload-postcode"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="plan-project-type">Project Type</Label>
            <select
              id="plan-project-type"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.projectType}
              onChange={(e) => setFormData({...formData, projectType: e.target.value})}
              data-testid="plan-upload-project-type"
            >
              <option value="">Select project type</option>
              <option value="rewire">Full Rewire</option>
              <option value="extension">Extension</option>
              <option value="renovation">Renovation</option>
              <option value="new-build">New Build</option>
              <option value="kitchen">Kitchen</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <Label htmlFor="plan-message">Additional Details</Label>
            <textarea
              id="plan-message"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Tell us about your project..."
              data-testid="plan-upload-message"
            />
          </div>

          <Button
            type="submit"
            disabled={uploading || files.length === 0}
            className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white"
            data-testid="plan-upload-submit"
          >
            {uploading ? 'Uploading...' : 'Submit Plans for Quote'}
          </Button>
        </form>
      </div>
    </div>
  );
};
